import type { ComponentMaterialGroup } from "@stryker/core/types/jobs";
import { type ChangeEvent, Fragment } from "react";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { TextInput } from "@/components/ui/textInput";
import { Typography } from "@/components/ui/typography";
import { inputStep, maxInputNumber } from "@/constants/inputs";
import { useConfigOptions } from "@/hooks/useConfigOptions";
import { cn } from "@/lib/utils";

import type { SingleComponentData } from "../types";

// Dummy flange rating options
const flangeRatingOptions = [
  { title: "150#", value: "150" },
  { title: "300#", value: "300" },
  { title: "600#", value: "600" },
  { title: "900#", value: "900" },
  { title: "1500#", value: "1500" },
  { title: "2500#", value: "2500" },
];

type FieldErrors = {
  component_nps?: string;
  flange_rating?: string;
  component_diameter?: string;
  unit_for_component_diameter?: string;
  component_thickness?: string;
  unit_for_component_thickness?: string;
  // Flange-specific field errors
  raised_face_od?: string;
  flange_num_bolt_holes?: string;
  flange_stud_size?: string;
  flange_bolt_circle?: string;
};

type FlangeClampComponentProps = {
  components_data: SingleComponentData[];
  set_components_data: React.Dispatch<React.SetStateAction<SingleComponentData[]>>;
  component_material: number | null;
  setComponent_material: React.Dispatch<React.SetStateAction<number | null>>;
  materialGroups: ComponentMaterialGroup[];
  materialError?: string | null;
  componentError?: FieldErrors;
};

const FlangeClampComponent = ({
  components_data,
  set_components_data,
  component_material,
  setComponent_material,
  materialGroups,
  materialError,
  componentError,
}: FlangeClampComponentProps) => {
  const { npsOptions } = useConfigOptions();

  const handleChangeData = (value: unknown, place: string) =>
    set_components_data((prevState) => {
      return prevState.map((p, i) => {
        if (i === 0) {
          return {
            ...p,
            [place]: value,
          };
        }
        return p;
      });
    });

  const component = components_data[0];
  const fieldWidth = "w-full sm:w-[270px]";

  !npsOptions.some((d) => d.title === "custom") && npsOptions.unshift({ title: "custom", value: "custom" });

  return (
    <div className="flex flex-col">
      <Label className="mb-1">
        Component Material <span className="text-red-500">*</span>
      </Label>
      <div className={fieldWidth}>
        <Select
          value={component_material !== null ? String(component_material) : undefined}
          onValueChange={(option) => {
            setComponent_material(Number(option));
          }}
        >
          <SelectTrigger
            className={cn(
              "w-full",
              !component_material && "[&>span]:text-gray-400",
              materialError && "border-error-300"
            )}
          >
            <SelectValue placeholder="Component Material" className="text-red-950" />
          </SelectTrigger>
          <SelectContent>
            {materialGroups.map((group, groupIndex) => (
              <Fragment key={group.group}>
                {groupIndex > 0 && <SelectSeparator />}
                <SelectGroup>
                  <SelectLabel>{group.group}</SelectLabel>
                  {group.options.map((option) => {
                    const descriptor = option.type ?? option.general_material;
                    const label = descriptor ? `${option.material} - ${descriptor}` : option.material;
                    return (
                      <SelectItem key={option.id} value={String(option.id)}>
                        {label}
                      </SelectItem>
                    );
                  })}
                </SelectGroup>
              </Fragment>
            ))}
          </SelectContent>
        </Select>
      </div>
      {materialError ? (
        <Typography className="mt-1 text-error-500" variant="text-small">
          {materialError}
        </Typography>
      ) : null}

      <div className="my-2 flex flex-wrap gap-4 pb-2">
        <div className="my-2 flex w-full items-end gap-4">
          <Typography variant="display-xs" weight="semibold">
            Component
          </Typography>
        </div>

        <div className={cn("flex flex-col", fieldWidth)}>
          <Label className="mb-1">NPS 1</Label>
          <Select
            value={component.component_nps ?? undefined}
            onValueChange={(option) => {
              handleChangeData(option, "component_nps");
              if (option === "custom") {
                // Clear flange rating if switching to custom NPS
                handleChangeData(null, "flange_rating");
                handleChangeData("Inches", "unit_for_component_diameter");
              } else {
                // Clear custom fields when switching away from custom
                handleChangeData(null, "component_diameter");
                handleChangeData(null, "unit_for_component_diameter");
                handleChangeData(null, "component_thickness");
                handleChangeData(null, "unit_for_component_thickness");
                // Clear flange-specific fields
                handleChangeData(null, "raised_face_od");
                handleChangeData(null, "flange_num_bolt_holes");
                handleChangeData(null, "flange_stud_size");
                handleChangeData(null, "flange_bolt_circle");
              }
            }}
          >
            <SelectTrigger
              className={cn(
                "w-full",
                !component.component_nps && "[&>span]:text-gray-400",
                componentError?.component_nps && "border-error-300"
              )}
            >
              <SelectValue placeholder="NPS" />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectLabel>NPS</SelectLabel>
                {npsOptions.map((option) => {
                  return (
                    <SelectItem key={option.value} value={String(option.value)}>
                      {option.title}
                    </SelectItem>
                  );
                })}
              </SelectGroup>
            </SelectContent>
          </Select>
          {componentError?.component_nps ? (
            <Typography className="mt-1 text-error-500" variant="text-small">
              {componentError.component_nps}
            </Typography>
          ) : null}
        </div>

        {component.component_nps === "custom" ? (
          <>
            <TextInput
              className={fieldWidth}
              label="Custom Diameter"
              type="number"
              name="component_diameter"
              step={inputStep}
              required={true}
              max={maxInputNumber}
              value={component.component_diameter ?? ""}
              onChange={(event: ChangeEvent<HTMLInputElement>) => {
                handleChangeData(event.target.value, "component_diameter");
              }}
              error={componentError?.component_diameter}
              endAdornment={<span className="ml-2 text-sm text-gray-500">in</span>}
            />

            <div className={cn("flex flex-col", fieldWidth)}>
              <Label className="mb-1 flex items-center justify-between text-sm text-gray-700">
                <span>Raised Face OD</span>
                <span className="text-xs text-gray-500">Optional</span>
              </Label>
              <TextInput
                className={fieldWidth}
                type="number"
                name="raised_face_od"
                step={inputStep}
                max={maxInputNumber}
                placeholder="Enter Raised Face OD"
                value={component.raised_face_od ?? ""}
                onChange={(event: ChangeEvent<HTMLInputElement>) => {
                  handleChangeData(event.target.value, "raised_face_od");
                }}
                error={componentError?.raised_face_od}
                endAdornment={<span className="ml-2 text-sm text-gray-500">in</span>}
              />
            </div>

            <div className={cn("flex flex-col", fieldWidth)}>
              <Label className="mb-1 flex items-center justify-between text-sm text-gray-700">
                <span>
                  Flange Num Bolt Holes <span className="text-red-500">*</span>
                </span>
              </Label>
              <TextInput
                className={fieldWidth}
                type="number"
                name="flange_num_bolt_holes"
                step="1"
                max={maxInputNumber}
                placeholder="Enter number of bolt holes"
                required={true}
                value={component.flange_num_bolt_holes ?? ""}
                onChange={(event: ChangeEvent<HTMLInputElement>) => {
                  handleChangeData(event.target.value, "flange_num_bolt_holes");
                }}
                error={componentError?.flange_num_bolt_holes}
              />
            </div>

            <div className={cn("flex flex-col", fieldWidth)}>
              <Label className="mb-1 flex items-center justify-between text-sm text-gray-700">
                <span>
                  Flange Stud Size <span className="text-red-500">*</span>
                </span>
              </Label>
              <TextInput
                className={fieldWidth}
                type="number"
                name="flange_stud_size"
                step={inputStep}
                max={maxInputNumber}
                placeholder="Enter stud size"
                required={true}
                value={component.flange_stud_size ?? ""}
                onChange={(event: ChangeEvent<HTMLInputElement>) => {
                  handleChangeData(event.target.value, "flange_stud_size");
                }}
                error={componentError?.flange_stud_size}
                endAdornment={<span className="ml-2 text-sm text-gray-500">in</span>}
              />
            </div>

            <div className={cn("flex flex-col", fieldWidth)}>
              <Label className="mb-1 flex items-center justify-between text-sm text-gray-700">
                <span>
                  Flange Bolt Circle <span className="text-red-500">*</span>
                </span>
              </Label>
              <TextInput
                className={fieldWidth}
                type="number"
                name="flange_bolt_circle"
                step={inputStep}
                max={maxInputNumber}
                placeholder="Enter bolt circle"
                required={true}
                value={component.flange_bolt_circle ?? ""}
                onChange={(event: ChangeEvent<HTMLInputElement>) => {
                  handleChangeData(event.target.value, "flange_bolt_circle");
                }}
                error={componentError?.flange_bolt_circle}
                endAdornment={<span className="ml-2 text-sm text-gray-500">in</span>}
              />
            </div>
          </>
        ) : null}

        {component.component_nps !== "custom" ? (
          <div className={cn("flex flex-col", fieldWidth)}>
            <Label className="mb-1">Flange Rating</Label>
            <Select
              value={component.flange_rating ? String(component.flange_rating) : undefined}
              onValueChange={(option) => {
                handleChangeData(option, "flange_rating");
              }}
            >
              <SelectTrigger
                className={cn(
                  "w-full",
                  !component.flange_rating && "[&>span]:text-gray-400",
                  componentError?.flange_rating && "border-error-300"
                )}
              >
                <SelectValue placeholder="Flange Rating" />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectLabel>Flange Rating</SelectLabel>
                  {flangeRatingOptions.map((option) => {
                    return (
                      <SelectItem key={option.value} value={String(option.value)}>
                        {option.title}
                      </SelectItem>
                    );
                  })}
                </SelectGroup>
              </SelectContent>
            </Select>
            {componentError?.flange_rating ? (
              <Typography className="mt-1 text-error-500" variant="text-small">
                {componentError.flange_rating}
              </Typography>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default FlangeClampComponent;
